import { css, Box, IconButton, styled } from "@mui/material";
import ZigTypography from "../ZigTypography";
import { withAttrs } from "../../../utils/styled";
import { isCapacitorIos } from "../../../utils/capacitor";

export const Layout = styled(Box, {
  shouldForwardProp: (prop) => prop !== "mobileFullScreen",
})<{
  width: number;
  mobileFullScreen?: boolean;
}>`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.backgrounds.neutralBg || theme.palette.neutral800};
  border: 1px solid ${(props) => props.theme.palette.neutral600};
  border-radius: 16px;
  padding: 40px 56px;

  &:focus-visible {
    outline: none;
  }

  max-width: 100%;
  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `};

  ${({ mobileFullScreen, width, theme }) =>
    (mobileFullScreen || width) &&
    css`
      @media (max-width: ${width || theme.breakpoints.values.sm}px) {
        width: 100%;
        border-radius: 0;
        border-left-width: 0;
        border-right-width: 0;
      }
    `};

  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    padding: 23px 23px 30px;
    ${({ mobileFullScreen }) =>
      mobileFullScreen &&
      css`
        padding-top: calc(23px + env(safe-area-inset-top));
        padding-bottom: ${isCapacitorIos ? 30 : 0}px;
        justify-content: flex-start;
        height: 100%;
        border-radius: 0;
        border-top-width: 0;
        border-bottom-width: 0;

        ${isCapacitorIos &&
        css`
          #modal__close,
          #modal__back {
            margin-top: calc(env(safe-area-inset-top));
          }
        `}
      `}
  }
`;

export const Title = styled(ZigTypography)`
  display: flex;
  justify-content: space-between;
  text-transform: capitalize;
  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    padding-left: 30px;
    padding-right: 30px;
    text-align: center;
  }
`;

export const Body = styled("div", {
  shouldForwardProp: (prop) => prop !== "mobileFullScreen",
})<{ mobileFullScreen?: boolean }>`
  font-size: 14px;
  text-align: left;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.palette.neutral200};

  max-height: calc(100vh - 150px);
  overflow-y: auto;
  overflow-x: visible;

  margin-left: -40px;
  margin-right: -40px;
  width: calc(100% + 80px);
  padding-left: 40px;
  padding-right: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
    padding-left: 20px;
    padding-right: 20px;
  }

  /* Style Description  */
  > .MuiTypography-root:first-of-type {
    margin-bottom: 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    ${({ mobileFullScreen }) =>
      mobileFullScreen &&
      css`
        max-height: 100%;
        padding-bottom: 30px;
      `}
  }
`;

export const Header = styled(Box, { shouldForwardProp: (p) => p !== "compact" })<{
  compact: boolean;
}>`
  display: flex;
  z-index: 996;
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin-bottom: 18px;
  ${({ compact }) =>
    compact &&
    `
    width: 32px;
    position: absolute;
    right: 56px;
    top: 40px;
  `}
`;

export const Inline = styled("div")<{ align?: string }>`
  ${({ align }) => `
    ${
      align === "center"
        ? `
      flex: 1;
      `
        : ``
    }
  `}
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

export const ZigModalActions = styled(
  withAttrs(Box, {
    mt: "28px",
  }),
)<{
  align?: "left" | "center" | "right";
  direction?: "row" | "column";
}>`
  display: flex;
  align-items: center;
  gap: 32px;
  flex-direction: ${({ direction }) => direction};
  width: 100%;
  justify-content: ${({ align }) =>
    align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center"};
`;

export const ZigModalForm = styled(
  withAttrs(Box, {
    component: "form",
    display: "flex",
    flexDirection: "column",
    gap: 3,
  }),
)``;

export const BackIconButton = styled(IconButton)`
  position: absolute;
  left: 22px;
  z-index: 999;
  top: 16px;
`;

export const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 16px;
  z-index: 999;
  top: 16px;
`;
